import { Widget, ViewWidgetHelper, ViewCmdType, encodeValues, DomHelpers, ImViewBridge, TableWidgetHelper } from '@nordbox/core';
import { createPopper } from '@popperjs/core';

// components/FileManagerWidget/FileManagerWidget.ts
// Перепись $.widget("ui.imFilemanager") на наш Widget без jQuery.
// Интеграции: ResizerWidget, TableWidget, VSPlayerWidget (seek, time-sync), TabsWidget, Popper для year-tip.
/**
 * Виджет файлового менеджера.
 * Ищется по data-widget="FileManager" или классу "im-filemanager" на корневом контейнере.
 */
class FileManagerWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Ширина текста для анимации подписи папки */
        this._nodeWidth = 0;
        // Очистки year-tip (Popper)
        this._yearTipCleanups = [];
        // ───────────────────────────── обработчики ─────────────────────────────
        this._onKeyCtrlArrows = (e) => {
            if (e.ctrlKey && (e.key === "ArrowLeft" || e.key === "ArrowRight"))
                e.preventDefault();
        };
        this._onPopState = () => this.onPopstate();
        this._onResize = () => {
            try {
                this.onCheckWidth();
            }
            catch (e) {
                console.error("onCheckWidth", e);
            }
        };
        this._onGoToFolder = (e) => {
            const detail = e.options || {};
            const btn = detail.button ?? null;
            const ui = btn?.dataset.guid || "";
            this._searchInput.value = "";
            this._searchbar.classList.remove("focused");
            this._btnClearSearchResult.style.display = "none";
            const group = this.element.querySelector('[data-key="_tpk_foldersTable__groupID"]');
            if (group) {
                group.value = ui;
                group.dispatchEvent(new CustomEvent("change", {
                    bubbles: true,
                    detail: {
                        breadcrumbsClick: true
                    }
                }));
            }
        };
        // ───────────────────────────── обработчики ─────────────────────────────
        /**
         * Смена текущей коллекции из легаси-шины.
         * Полный аналог:
         *  - обновить hidden contextCurrentFileGroupsCollection
         *  - reload таблицы foldersTable
         *  - reload fileManagerProjectsView (refresh)
         *  - ExecItemCmd('changeCurrentCollection', { currentCollection: ui })
         */
        this._onCurrentFileGroupsChanged = (e) => {
            const btn = e.options?.button;
            const ui = btn?.dataset.value || btn?.value;
            if (!ui || ui === this.guidEmpty)
                return;
            // Меняем значения hiden элментов
            const ctxInputs = document.body.querySelectorAll('[data-key="contextCurrentFileGroupsCollection"]');
            ctxInputs.forEach(input => {
                input.value = ui;
            });
            this._reloadTable();
            // reload fileManagerProjectsView (эквивалент 'refresh')
            const projects = this.element.querySelector('[data-key="fileManagerProjectsView"]');
            if (projects) {
                ViewWidgetHelper.reload(projects, { reloadType: ViewCmdType.refresh });
            }
            // ExecItemCmd
            const hostView = ViewWidgetHelper.closestOnByName(this.element)?.element;
            if (hostView) {
                ViewWidgetHelper.ExecItemCmd(this.element, {
                    cmd: "changeCurrentCollection",
                    options: { currentCollection: ui }
                }, hostView);
            }
        };
        this.guidEmpty = "00000000-0000-0000-0000-000000000000";
        /** Подавляет повторный reload до завершения предыдущего */
        this._asideReloadLock = false;
    }
    static _cssKey() { return "im-filemanager"; }
    // ───────────────────────────── lifecycle ─────────────────────────────
    /**
     * Инициализация виджета.
     */
    _create(_) {
        // найти элементы
        this._fmSearch = this._req(".filemanager-search");
        this._fmFilter = this._reqWithin(this._fmSearch, ".filemanager-filter");
        this._searchbar = this._reqWithin(this._fmSearch, ".filemanager-search__seachbar");
        this._folder = this._reqWithin(this._fmSearch, ".filemanager-search__folder");
        this._folderName = this._reqWithin(this._fmSearch, ".filemanager-search__folder-name");
        this._searchInput = this._reqWithin(this._fmSearch, '[data-key="searchInput"]');
        this._btnOpenFilter = this._reqWithin(this._fmSearch, '[data-key="btnOpenFilter"]');
        this._btnClearSearchResult = this._reqWithin(this._fmSearch, '[data-key="btnClearFilter"]');
        this._wrap = this._req(".filemanager-wrapper");
        this._btnApplyFilters = this._req('[data-key="btnApplyFilters"]');
        this._btnCancelFilters = this._req('[data-key="btnCancelFilters"]');
        this._fileManagerFilter = this._req('[data-key="fileManagerFilterView"]');
        this._sideInfoWrapper = this._req('[data-key="fileManagerSideInfoWrapperView"]');
        this._foldersTable = this._req('[data-tablekey="foldersTable"]');
        this._abortController = new AbortController();
        // URL init / prevState
        try {
            const url = new URL(window.location.href);
            this._prevState = url;
            window.addEventListener("keydown", this._onKeyCtrlArrows);
            window.addEventListener("popstate", this._onPopState);
            ['tree-toggled', 'sideinfo-toggled', 'tree-group-clicked'].forEach(eventName => {
                document.body.addEventListener(eventName, () => this._refreshResizer(), { signal: this._abortController.signal });
            });
            // Плеер (подписки на его события)
            this._wireVsPlayer();
            // PlayTime → seek плееру только если opened=true/присутствует, иначе стереть
            if (url.searchParams.has("PlayTime")) {
                const t = parseFloat(url.searchParams.get("PlayTime"));
                const hasOpened = url.searchParams.has("opened");
                const openedVal = url.searchParams.get("opened");
                const isOpened = hasOpened || openedVal === "true";
                if (isOpened) {
                    this._seekVSPlayerFrom(this._wrap, t);
                }
                else {
                    this._changeUrl({ PlayTime: "" });
                }
            }
        }
        catch (e) {
            console.error("filemanagerinit error", e);
        }
        // Вкладки: наш TabsWidget (без jQuery)
        this._wireTabs(this._sideInfoWrapper);
        // фильтр: применить / сбросить
        this._btnApplyFilters.addEventListener("click", () => {
            const values = encodeValues(this._fileManagerFilter);
            this._changeUrl(values);
            this._fmFilter.classList.remove("active");
            this._doSearch();
        });
        this._btnCancelFilters.addEventListener("click", () => {
            this._fileManagerFilter
                .querySelectorAll('[type="checkbox"]')
                .forEach(c => (c.checked = false));
            this._fileManagerFilter
                .querySelectorAll('[type="text"]')
                .forEach(t => (t.value = ""));
            const values = encodeValues(this._fileManagerFilter);
            this._changeUrl(values);
        });
        // Закрытие фильтра кликом вне + откат значений из URL
        this._onDocDownFilter = (event) => {
            const t = event.target;
            const clickedInside = t.closest(".filemanager-filter") || t.closest('[data-key="btnOpenFilter"]');
            if (clickedInside)
                return;
            this._fmFilter.classList.remove("active");
            const url = new URL(window.location.href);
            const searchFields = url.searchParams.get("fields");
            this._updateFilter(this._fileManagerFilter.querySelector('[data-key="fields"]'), searchFields);
        };
        document.addEventListener("mousedown", this._onDocDownFilter);
        // поиск: фокус/blur/ввод/enter/change
        this._searchInput.addEventListener("focusin", () => {
            this._searchbar.classList.add("focused");
            this._folderName.classList.add("active");
            this.calcTextWidth(this._folderName.textContent || "");
            this._folder.style.setProperty("--width", `${this._nodeWidth + 48 + 4}px`);
        });
        this._searchInput.addEventListener("focusout", () => {
            this._searchbar.classList.remove("focused");
            setTimeout(() => {
                if (!this._fmFilter.classList.contains("active")) {
                    this._folderName.classList.remove("active");
                    this._folder.style.setProperty("--width", "48px");
                }
            }, 300);
        });
        // Принудительный сброс фокуса с input если клик вне блока
        this.element.addEventListener("click", (e) => {
            const target = e.target;
            if (!target.closest(".filemanager-search"))
                this._searchInput.blur();
        });
        // Показ/скрытие крестика
        if (this._searchInput.value)
            this._btnClearSearchResult.style.display = "flex";
        this._searchInput.addEventListener("input", () => {
            this._btnClearSearchResult.style.display = this._searchInput.value ? "flex" : "none";
        });
        // Enter прячет деталь, change запускает поиск и сбрасывает страницу
        this._searchInput.addEventListener("keyup", (e) => {
            if (e.key === "Enter" && this._wrap.offsetParent !== null) {
                this._wrap.style.display = "none";
                this._changeUrl({
                    opened: false,
                    PlayTime: ""
                });
            }
        });
        this._searchInput.addEventListener("change", () => {
            const pn = this.element.querySelector(".im-pagenumbox");
            if (pn) {
                pn.value = "1";
            }
            this._doSearch();
        });
        // Тумблер фильтра
        this._btnOpenFilter.addEventListener("click", () => {
            const willOpen = !this._fmFilter.classList.contains("active");
            this._fmFilter.classList.toggle("active", willOpen);
            if (willOpen)
                this._searchInput.focus();
        });
        // Кнопка очистки поиска
        this._btnClearSearchResult.addEventListener("click", () => {
            this._searchInput.value = "";
            this._searchbar.classList.remove("focused");
            this._doSearch();
            this._btnClearSearchResult.style.display = "none";
        });
        // Кнопка «сброс папки»
        this.element.querySelector(".filemanager-search__folder-cancel")?.addEventListener("click", () => {
            this._folder.style.display = "none";
            this._folder.dataset.node = "";
            this._folder.dataset.root = "";
            this._setBreadCrumbs(true);
            this.element.querySelector('[data-key="foldersTableTree"] ._cur')?.classList.remove("_cur");
            this._changeUrl({ folder: "" });
            const tpk = this.element.querySelector('[data-key="_tpk_foldersTable__groupID"]');
            if (tpk)
                tpk.value = "";
            this._doSearch();
        });
        // Сворачиватели
        const leftToggler = this.element.querySelector(".filemanager__tree-toggler");
        leftToggler?.addEventListener("click", () => {
            const left = leftToggler.closest(".filemanager__left");
            left?.classList.toggle("filemanager__left_collapsed");
            this._refreshResizer(left);
            this.element.dispatchEvent(new Event("tree-toggled", { bubbles: true }));
        });
        this.element.addEventListener("click", (ev) => {
            const sideToggler = ev.target.closest(".file-sideinfo__toggler");
            if (!sideToggler)
                return;
            const side = sideToggler.closest(".file-sideinfo");
            side?.classList.toggle("file-sideinfo_collapsed");
            this._refreshResizer(side);
            this.element.dispatchEvent(new Event("sideinfo-toggled", { bubbles: true }));
        });
        // Таблица: текущая/выбранные/пагинация/размер/смена папки
        const currentRowInput = this.element.querySelector('[data-key="_tpk_foldersTable__currentRow"]');
        currentRowInput?.addEventListener("change", () => { });
        const checkedRowsInput = this.element.querySelector('[data-key="_tpk_foldersTable__checkedRows"]');
        checkedRowsInput?.addEventListener("change", (e) => {
            if (this._isEditMode())
                return;
            this._onCheckedRowsChange(checkedRowsInput.value);
        });
        const pageNum = this.element.querySelector('[data-key="_tpk_foldersTable__curPage"]');
        pageNum?.addEventListener("change", () => {
            if (this._isEditMode())
                return;
            const val = pageNum.value === "1" ? "" : pageNum.value;
            this._changeUrl({ curPage: val });
        });
        const maxRows = this.element.querySelector('[data-key="_tpk_foldersTable__maxRows"]');
        maxRows?.addEventListener("change", () => {
            if (this._isEditMode())
                return;
            this._changeUrl({ maxRows: maxRows.value });
        });
        const groupId = this.element.querySelector('[data-key="_tpk_foldersTable__groupID"]');
        groupId?.addEventListener("change", (e) => {
            if (this._isEditMode())
                return;
            const ui = groupId.value || this.guidEmpty;
            this._groupID = ui;
            // выставляем в правой панели оба инпута (полный паритет со старым кодом)            
            this._sideInfoWrapper.querySelectorAll('[data-key="fileGroupList"],[data-key="FileGroupUI"]').forEach((elem) => {
                elem.value = ui;
            });
            const breadcrumbsClick = e?.detail?.breadcrumbsClick;
            this._searchInput.value = "";
            this._onChangeCurrentFolder(ui);
            this._searchbar.classList.remove("focused");
            DomHelpers.hide(this._btnClearSearchResult);
            this._doSearch(true, ui, breadcrumbsClick);
        });
        // Делегат на prev/next (во всплывающем UI тоже)
        this.element.addEventListener("click", (ev) => {
            const btn = ev.target.closest(".control-page");
            if (!btn)
                return;
            this.onArrowClick(btn);
        });
        // Делегаты для открытия/закрытия детальной панели на динамическом HTML
        this.element.addEventListener("click", (ev) => {
            const openBtn = ev.target.closest(".cmd-open-detailed");
            if (openBtn) {
                this.onCmdDetailedOpen(openBtn, openBtn.dataset.tooltype);
                return;
            }
            const closeBtn = ev.target.closest(".cmd-close-detailed");
            if (closeBtn) {
                this.closeDetailed();
            }
        });
        // «копировать ссылку» в динамически подгружаемом HTML
        this.element.addEventListener("click", async (ev) => {
            const btn = ev.target.closest(".cmd-copy-link");
            if (!btn)
                return;
            try {
                await navigator.clipboard.writeText(window.location.href);
            }
            catch (e) {
                console.error("copy link", e);
            }
        });
        // Реакция на клик по ноде дерева (совместимость)
        this.element.querySelector(".im-tree")?.addEventListener("imtree-node-click", () => {
            // визуально очистить поле и крестик
            this._searchInput.value = "";
            this._searchbar.classList.remove("focused");
            DomHelpers.hide(this._btnClearSearchResult);
            // закрыть деталь и сбросить curPage в URL (как раньше)
            this.closeDetailed({ curPage: "" });
        });
        // Обновление ресайзера при кликах в дереве (совместимость)
        this.element.querySelector(".im-tree")?.addEventListener("tree-group-clicked", () => {
            this._refreshResizer();
        });
        // Popper для year-tip
        this._wireYearTips();
        // глобальные «системные» события (от легаси-шины через ImViewBridge.execEvent → CustomEvent на body)
        document.body.addEventListener("go-to-folder", this._onGoToFolder, { signal: this._abortController.signal });
        document.body.addEventListener("onCurrentFileGroupsCollectionChanged", this._onCurrentFileGroupsChanged, { signal: this._abortController.signal });
        // первичная адаптация + resize
        try {
            this.onCheckWidth();
        }
        catch (e) {
            console.error("onCheckWidth", e);
        }
        window.addEventListener("resize", this._onResize, { passive: true });
    }
    // ───────────────────────────── helpers/DOM ─────────────────────────────
    /** Обязательный узел по селектору */
    _req(sel) {
        const n = this.element.querySelector(sel);
        if (!n)
            throw new Error(`[FileManager] Не найден узел ${sel}`);
        return n;
    }
    /** Обязательный узел внутри root по селектору */
    _reqWithin(root, sel) {
        const n = root.querySelector(sel);
        if (!n)
            throw new Error(`[FileManager] Не найден узел ${sel} внутри ${root.className}`);
        return n;
    }
    /** Режим редактирования (любой из двух флагов) */
    _isEditMode() {
        const d1 = this.element.querySelector('[data-key="_fv_descFieldsView_editMode"]')?.value === "True";
        const d2 = this.element.querySelector('[data-key="_fv_infoView_editMode"]')?.value === "True";
        return !!(d1 || d2);
    }
    /** Получить инстанс ResizerWidget по имени виджета */
    _getResizer(container) {
        const resizers = [];
        if (container) {
            const resizerEl = container.querySelector(".im-resizer");
            const inst = Widget.closestOnByName(resizerEl, "Resizer");
            console.log('inst', inst);
            if (inst)
                resizers.push(inst);
        }
        else {
            this.element.querySelectorAll(".im-resizer").forEach((elem) => {
                const inst = Widget.closestOnByName(elem, "Resizer");
                if (inst)
                    resizers.push(inst);
            });
        }
        return resizers;
    }
    /** Обновить положение и ширину ресайзера после изменений разметки */
    _refreshResizer(container) {
        this._getResizer(container)?.forEach((resizer) => {
            resizer.updateResizer();
        });
    }
    /** Получить инстанс TableWidget (alias "Table") */
    _getTable() {
        const host = this.element.querySelector('[data-tablekey="foldersTable"]');
        if (!host)
            return undefined;
        return Widget.closestOnByName(host, "Table");
    }
    /** Подключить события плеера (на его элементах; событие не всплывает) */
    _wireVsPlayer() {
        if (this._playerAbortController)
            this._playerAbortController.abort();
        // Ищем плеер преимущественно в detailed-области (wrap), но на всякий случай и в корне
        const roots = [this._wrap, this.element].filter(Boolean);
        for (const root of roots) {
            const player = root.querySelector(".im-vs-player");
            if (player) {
                const fn = (ev) => {
                    const tc = ev.detail?.timecode;
                    if (typeof tc === "number" && Number.isFinite(tc)) {
                        this._changeUrl({ PlayTime: String(tc) });
                    }
                };
                this._playerAbortController = new AbortController();
                player.addEventListener("player-time-changed", fn, { signal: this._playerAbortController.signal });
            }
        }
    }
    /** Найти инстанс VSPlayer и выполнить seek(sec) */
    _seekVSPlayerFrom(root, sec) {
        if (!Number.isFinite(sec))
            return;
        const playerEl = root.querySelector(".im-vs-player");
        if (!playerEl)
            return;
        const inst = Widget.closestOnByName(playerEl, "VSPlayer");
        if (inst)
            inst.seek(sec);
    }
    /** Проводка нашего TabsWidget: подписка и стартовое применение из URL */
    _wireTabs(root) {
        const urlTab = new URL(window.location.href).searchParams.get("tab") || undefined;
        root.querySelectorAll(".im-jqtabs").forEach(host => {
            const inst = Widget.closestOnByName(host, "Tabs");
            if (!inst)
                return;
            // применим стартовую вкладку из URL (если задана)
            if (urlTab)
                inst.setValue(urlTab);
            // при смене вкладки — обновляем URL и скрытые поля, если используются
            const onActivate = (ev) => {
                const detail = ev.detail;
                const id = detail?.newPanel?.id;
                if (id) {
                    this._changeUrl({ tab: id });
                    root
                        .querySelectorAll('[data-key="infoTabIndex"]')
                        .forEach(x => (x.value = id));
                }
            };
            host.addEventListener("tabsactivate", onActivate);
        });
    }
    // ───────────────────────────── публичные/внешние ─────────────────────────────
    /**
     * Восстановление UI из URL и условный reload.
     */
    onPopstate() {
        const el = this.element;
        try {
            const newUrl = new URL(window.location.href);
            if (this._prevState && this._prevState.search === newUrl.search)
                return;
            const oldParams = this._prevState?.searchParams ?? new URLSearchParams();
            const params = newUrl.searchParams;
            this._prevState = newUrl;
            const filter = this._fileManagerFilter;
            let doReload = false;
            if (oldParams.get("folder") !== params.get("folder")) {
                const folder = params.get("folder") || "";
                const group = el.querySelector('[data-key="_tpk_foldersTable__groupID"]');
                if (group)
                    group.value = folder;
                doReload = true;
            }
            const putFolder = () => { this._folder.dataset.node = params.get("folder") || ""; };
            if (oldParams.get("fg") !== params.get("fg")) {
                const ui = params.get("fg") ?? "";
                const cur = el.querySelector('[data-key="_tpk_foldersTable__currentRow"]');
                if (cur)
                    cur.value = ui;
                doReload = true;
            }
            {
                const text = params.get("txt") || "";
                const input = el.querySelector('[data-key="searchInput"]');
                if (input)
                    input.value = text;
                if (oldParams.get("txt") !== params.get("txt")) {
                    doReload = true;
                }
            }
            if (oldParams.get("opened") !== params.get("opened")) {
                const hasOpened = params.has("opened");
                const openedVal = params.get("opened");
                const isOpened = hasOpened || openedVal === "true";
                if (isOpened) {
                    this._wrap.style.display = "";
                    this._wireVsPlayer(); // перехватить события на свежем DOM
                    this._wireYearTips();
                    this._wireTabs(this._sideInfoWrapper);
                }
                else {
                    this.closeDetailed();
                }
            }
            if (oldParams.get("curPage") !== params.get("curPage")) {
                const curPage = params.get("curPage") || "1";
                const pn = el.querySelector(".im-pagenumbox");
                if (pn && pn.value != curPage) {
                    pn.value = curPage;
                    doReload = true;
                }
            }
            // Применяем вкладку из URL через TabsWidget
            if (oldParams.get("tab") !== params.get("tab")) {
                const tab = params.get("tab") || "tab1";
                this._sideInfoWrapper
                    .querySelectorAll('[data-key="infoTabIndex"]')
                    .forEach(x => (x.value = tab));
                this._sideInfoWrapper.querySelectorAll(".im-jqtabs").forEach(host => {
                    const inst = Widget.closestOnByName(host, "Tabs");
                    inst?.setValue(tab);
                });
            }
            if (oldParams.get("PlayTime") !== params.get("PlayTime")) {
                const tStr = params.get("PlayTime");
                const t = tStr != null ? parseFloat(tStr) : NaN;
                if (Number.isFinite(t))
                    this._seekVSPlayerFrom(this._wrap, t);
            }
            // параметры фильтра
            if (oldParams.get("fields") !== params.get("fields")) {
                this._updateFilter(filter.querySelector('[data-key="fields"]'), params.get("fields"));
                doReload = true;
            }
            if (oldParams.get("shootingDateFrom") !== params.get("shootingDateFrom")) {
                this._updateFilter(filter.querySelector('[data-key="shootingDateFrom"]'), params.get("shootingDateFrom"));
                doReload = true;
            }
            if (oldParams.get("shootingDateTill") !== params.get("shootingDateTill")) {
                this._updateFilter(filter.querySelector('[data-key="shootingDateTill"]'), params.get("shootingDateTill"));
                doReload = true;
            }
            if (oldParams.get("searchInNestedFolders") !== params.get("searchInNestedFolders")) {
                const chk = filter.querySelector('[data-key="searchInNestedFolders"]');
                if (chk)
                    chk.checked = params.has("searchInNestedFolders");
                doReload = true;
            }
            const cb = () => {
                putFolder();
            };
            if (doReload) {
                ViewWidgetHelper.reload(this.element, {
                    onCallback: cb,
                    reloadType: ViewCmdType.reload
                });
            }
        }
        catch (e) {
            console.error("onpopstate", e);
        }
    }
    /**
     * Закрыть подробную панель и синхронизировать URL.
     */
    closeDetailed(urlParams) {
        this._wrap.style.display = "none";
        ImViewBridge.execEvent(this.element, "close-vsplayer"); // PlayerBaseWidget слушает на body
        const base = { opened: false, PlayTime: "" };
        this._changeUrl({ ...base, ...(urlParams ?? {}) });
    }
    // ───────────────────────────── легаси-логика в TS ─────────────────────────────
    /** Значение GUID-пустышки */
    _isNull(ui) {
        return !ui || ui === "" || ui === "00000000-0000-0000-0000-000000000000";
    }
    /** Включить/выключить «крошки» */
    _setBreadCrumbs(isAll) {
        const all = this.element.querySelector(".filemanager__breadcrumbs.all");
        const paths = this.element.querySelector(".filemanager__breadcrumbs.paths");
        if (!all || !paths)
            return;
        if (isAll) {
            DomHelpers.show(all);
            DomHelpers.hide(paths);
        }
        else {
            DomHelpers.show(paths);
            DomHelpers.hide(all);
        }
    }
    /** Обработка смены текущей папки (обновление «крошек», заголовка, правой панели) */
    _onChangeCurrentFolder(ui) {
        const folder = this._folder;
        let label = "";
        this._setBreadCrumbs();
        const cur = this.element.querySelector(".filemanager__tree ._cur");
        if (this._isNull(ui)) {
            if (cur?.dataset.id) {
                cur.classList.remove("_cur");
                this.element.querySelector(".filemanager__tree [data-nodekey='']")?.classList.add("_cur");
            }
            label = "root";
            folder.dataset.node = "";
            folder.dataset.root = "True";
        }
        else {
            if (cur?.dataset.id !== ui) {
                cur?.classList.remove("_cur");
                this.element.querySelector(`.filemanager__tree [data-nodekey='${ui}']`)?.classList.add("_cur");
            }
            const labelItem = this.element.querySelector(`.filemanager__tree [data-nodekey='${ui}'] > .parent-node label`);
            if (labelItem) {
                label = labelItem.innerHTML;
            }
            else {
                const hostView = this.element.closest(".im-view");
                if (hostView) {
                    ViewWidgetHelper.ExecItemCmd(this.element, {
                        cmd: "getGroupTitle",
                        options: { groupUI: ui },
                        onSuccess: (cmd) => {
                            const title = cmd?.groupTitle ?? "";
                            folder.dataset.node = ui;
                            folder.dataset.root = "";
                            this._folderName.textContent = title;
                        },
                        onError: (cmd) => console.error(cmd?.type, cmd?.msg)
                    }, hostView);
                }
            }
            folder.dataset.node = ui;
            folder.dataset.root = "";
        }
        folder.style.display = "";
        this._folderName.textContent = label;
        // перечитать «крошки» адреса папки
        const hostView = ViewWidgetHelper.closestOnByName(this.element)?.element;
        if (hostView) {
            ViewWidgetHelper.reload(hostView, {
                itemKeysToReload: "foldersPath"
            });
        }
    }
    /** Реакция на изменение выбранных строк */
    _onCheckedRowsChange(uiList) {
        let arrList;
        let lastInArr;
        let fileGroup;
        if (uiList) {
            arrList = uiList.split(",");
            lastInArr = arrList[arrList.length - 1];
            fileGroup = lastInArr;
        }
        else if (this._groupID) {
            fileGroup = this._groupID;
        }
        else {
            fileGroup = this.guidEmpty;
        }
        this.element.querySelectorAll('.file-sideinfo [data-key=fileGroupList]').forEach((elem) => {
            if (uiList)
                elem.value = uiList;
            else
                elem.value = fileGroup;
        });
        this.element.querySelectorAll('.file-sideinfo [data-key=FileGroupUI]').forEach((elem) => {
            elem.value = fileGroup;
        });
        const cb = () => {
            this._wireTabs(this._sideInfoWrapper);
            this._wireYearTips();
            this._changeUrl({
                fg: fileGroup
            });
        };
        this._reloadAside(cb);
    }
    /**
     * Открытие детальной панели/плеера по кнопке.
     * @param elem Кнопка внутри правой панели
     * @param toolType Тип тулзы (если есть)
     */
    onCmdDetailedOpen(elem, toolType) {
        const wrap = this._wrap;
        let ui = this._sideInfoWrapper?.querySelector('[data-key="FileGroupUI"]')?.value || "";
        const values = {
            contextCurrentFileGroupsCollection: this.element
                .querySelector('[data-key="CurrentFileGroupsCollection"]')
                ?.dataset.value || ''
        };
        if (elem.dataset.in) {
            const dataIn = elem.dataset.in.replace(",", ".");
            values.PlayTime = dataIn;
        }
        if (elem.dataset.fg)
            ui = elem.dataset.fg;
        if (this._isNull(ui)) {
            DomHelpers.hide(wrap);
            return;
        }
        const callback = () => {
            // после загрузки детальной — перевешиваем обработчики
            //Не уверен что здесь это нужно
            this._wireVsPlayer();
            //this._wireYearTips();
            //this._wireTabs(this._sideInfoWrapper);
            //this._wireResizer(); 
            this._changeUrl({ opened: true });
        };
        DomHelpers.show(wrap);
        wrap.querySelectorAll('[data-key="FileGroupUI"]').forEach((fileGroupInput) => {
            fileGroupInput.value = ui;
        });
        wrap.querySelectorAll('[data-key="toolType"]').forEach((toolTypeInput) => {
            toolTypeInput.value = toolType ?? "";
        });
        const ccfgc = wrap.querySelector('[data-key="contextCurrentFileGroupsCollection"]');
        if (ccfgc)
            ccfgc.value = String(values.contextCurrentFileGroupsCollection ?? "");
        ViewWidgetHelper.reload(wrap, {
            onCallback: callback,
            options: values
        });
    }
    /**
     * Применить значения фильтра в элементы (listbox/datepicker/input).
     * @param elem Узел фильтра
     * @param values Строка вида "{a}{b}{c}" или простая строка/пусто
     */
    _updateFilter(elem, values) {
        if (!elem)
            return;
        const isList = elem.classList.contains("im-listbox");
        const isDate = elem.classList.contains("im-datepicker");
        if (values && values !== "") {
            if (isList) {
                // "{a}{b}{c}" -> "a,b,c" без replaceAll
                const norm = values.replace(/\}\{/g, ",").replace(/[{}]/g, "");
                const arr = norm ? norm.split(",") : [];
                elem
                    .querySelectorAll('input[type="checkbox"]')
                    .forEach((i) => { i.checked = false; });
                arr.forEach((k) => {
                    const it = elem.querySelector(`[value="${k}"]`);
                    if (it)
                        it.checked = true;
                });
            }
            else if (isDate) {
                const d = new Date(values);
                if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, "0");
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    elem.value = `${dd}.${mm}.${d.getFullYear()}`;
                }
                else {
                    elem.value = values;
                }
            }
            else {
                elem.value = values;
            }
        }
        else {
            if (isList) {
                elem
                    .querySelectorAll('input[type="checkbox"]')
                    .forEach((i) => { i.checked = false; });
            }
            else {
                elem.value = "";
            }
        }
    }
    /** Ширина текста (для анимации подписи папки) */
    calcTextWidth(text) {
        const node = document.createElement("div");
        node.className = "fs14";
        node.style.position = "absolute";
        node.style.visibility = "hidden";
        node.textContent = text;
        document.body.appendChild(node);
        this._nodeWidth = node.getBoundingClientRect().width;
        node.remove();
    }
    /** Таблица (DOM-контейнер) — пригодится для фоллбека */
    connectedTable() {
        return this.element.querySelector(".filemanager__table-wrapper .im-table");
    }
    /** Проверка ширины экрана → автосворачивание левой/правой панелей */
    onCheckWidth() {
        if (document.body.clientWidth < 1440) {
            this.element.querySelector(".filemanager__left")?.classList.add("filemanager__left_collapsed");
            this.element.querySelector(".file-sideinfo")?.classList.add("file-sideinfo_collapsed");
        }
        else {
            this.element.querySelector(".filemanager__left")?.classList.remove("filemanager__left_collapsed");
            this.element.querySelector(".file-sideinfo")?.classList.remove("file-sideinfo_collapsed");
        }
        // Совместимость со старым кодом
        this.element.dispatchEvent(new Event("tree-toggled", { bubbles: true }));
        this.element.dispatchEvent(new Event("sideinfo-toggled", { bubbles: true }));
        this._refreshResizer();
    }
    /**
     * Клик по стрелкам prev/next во всплывающем UI.
     * Переключает строку таблицы и перезагружает детальную панель.
     */
    onArrowClick(button) {
        // Сбрасываем PlayTime в URL
        this._changeUrl({ PlayTime: "" });
        const newRow = button.classList.contains("prev") ?
            this._getTable()?.currentRow()?.previousElementSibling :
            this._getTable()?.currentRow()?.nextElementSibling;
        if (!newRow)
            return;
        this._getTable()?.currentRow(newRow);
        const rowID = newRow.dataset.id;
        if (!rowID)
            return;
        // Выставляем текущую строку таблицы публичным API
        const tableInst = this._getTable();
        tableInst?.currentRowByID(rowID, {
            append: false,
            scroll: true
        });
        // Синхронизируем детальную панель        
        this.element.querySelectorAll('.filemanager-detailed [data-key="FileGroupUI"]').forEach((elem) => {
            elem.value = rowID;
        });
        const detailed = this.element.querySelector(".filemanager-detailed");
        if (detailed) {
            ViewWidgetHelper.reload(detailed, {
                reloadType: ViewCmdType.reload
            });
        }
    }
    /** Правила применения значения параметра в URL */
    _checkSearchParam(url, param, value) {
        const empty = value === "dbnull" || value === false || value == null || value === "";
        if (empty && url.searchParams.has(param)) {
            url.searchParams.delete(param);
        }
        else if (!empty && url.searchParams.has(param)) {
            url.searchParams.set(param, String(value));
        }
        else if (!empty && !url.searchParams.has(param)) {
            url.searchParams.append(param, String(value));
        }
    }
    /** Установка/удаление параметров в URL и pushState */
    _changeUrl(values) {
        const url = new URL(window.location.href);
        for (const k in values)
            this._checkSearchParam(url, k, values[k]);
        if (this._prevState && this._prevState.search === url.search)
            return;
        // особый кейс PlayTime — заменяем запись в истории
        const playTimeChanged = (this._prevState?.searchParams.get("PlayTime") ?? "") !== (url.searchParams.get("PlayTime") ?? "");
        // fg — всегда пропускаем в истории
        const fgChanged = (this._prevState?.searchParams.get("fg") ?? "") !== (url.searchParams.get("fg") ?? "");
        if (playTimeChanged || fgChanged) {
            history.replaceState({}, "", url);
            this._prevState = url;
            return;
        }
        this._prevState = url;
        window.history.pushState({}, "", url);
    }
    /** Общий метод перезагрузки боковой панели */
    _reloadAside(cb) {
        // если предыдущая перезагрузка ещё не завершилась — выходим
        if (this._asideReloadLock)
            return;
        this._asideReloadLock = true;
        const wrappedCb = () => {
            try {
                cb?.();
            }
            finally {
                this._asideReloadLock = false;
            }
        };
        ViewWidgetHelper.reload(this._sideInfoWrapper, {
            onCallback: wrappedCb,
            reloadType: ViewCmdType.refresh
        });
    }
    /** Общий метод перезагрузки таблицы */
    _reloadTable() {
        TableWidgetHelper.reload(this._foldersTable);
    }
    /**
      * Выполнить поиск из строки и синхронизировать URL/виджеты.
      * При любом поиске принудительно закрывает детальную панель:
      *  - скрывает UI детальной области,
      *  - убирает opened/PlayTime из URL.
      * @param setFolderWithFG если true — дополнительно проставляет folder/fg в URL
      * @param ui UI активной папки (нужно при setFolderWithFG=true)
      */
    _doSearch(setFolderWithFG, ui, breadcrumbsClick) {
        // 1) Текущие значения поисковой области
        const text = this._searchInput.value;
        const folderNode = this._folder.dataset.node || "";
        const isRoot = this._folder.dataset.root === "True";
        // 2) Закрываем детальную панель на уровне UI (без pushState)
        if (this._wrap) {
            DomHelpers.hide(this._wrap);
        }
        // 3) Формируем патч для URL за один проход:
        //    - текст поиска,
        //    - опционально folder/fg,
        //    - всегда принудительно закрываем «деталь» (opened=false, PlayTime="")
        const urlPatch = setFolderWithFG
            ? {
                txt: text,
                folder: ui,
                fg: ui
            }
            : {
                txt: text
            };
        urlPatch.opened = false;
        urlPatch.PlayTime = "";
        this._changeUrl(urlPatch);
        // 4) Команда поиска (сервер): обновляем скрытые поля и перезагружаем таблицу/правую панель
        // command-contract: Nordbox.Storage/wwwroot/src_tests/contracts/file-manager-do-search.md — при изменении payload/route/DTO обновить contract, fixtures и TS/C# tests
        const op = {
            text,
            folder: this._isNull(folderNode) ? null : folderNode,
            isRoot
        };
        const view = ViewWidgetHelper.closestOnByName(this.element)?.element;
        if (!view)
            return;
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: "doSearch",
            options: op,
            cmdType: ViewCmdType.itemcmd,
            onSuccess: (data) => {
                // Обновляем скрытый инпут текущей поисковой строки
                const searchEl = this.element.querySelector('[data-key="search"]');
                if (searchEl) {
                    searchEl.value = data?.search ?? "";
                }
                if (!setFolderWithFG || breadcrumbsClick) {
                    this._reloadTable();
                }
                this._reloadAside();
            },
            onError: (err) => {
                console.error(err?.type ?? "request error", err?.msg ?? err);
            }
        }, view);
    }
    // ───────────────────────────── year-tip (Popper) ─────────────────────────────
    // TODO пересмотреть подписку на показ подсказок
    /**
     * Подключает подсказки ".year-tip" к ".year-tip-wrapper" через Popper.
     */
    _wireYearTips() {
        // снять прошлые
        for (const fn of this._yearTipCleanups)
            fn();
        this._yearTipCleanups = [];
        const wrappers = this.element.querySelectorAll(".year-tip-wrapper");
        wrappers.forEach((wrap) => {
            const tip = wrap.querySelector(".year-tip");
            if (!tip)
                return;
            // локальная ссылка на инстанс поппера
            let popper = null;
            const show = () => {
                if (popper)
                    return;
                // делаем видимой перед расчётом, чтобы Popper корректно посчитал размеры
                tip.style.visibility = "visible";
                tip.style.opacity = "1";
                popper = createPopper(wrap, tip, {
                    placement: "top-start",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 8]
                            }
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                padding: 8
                            }
                        },
                        {
                            name: "flip",
                            options: {
                                fallbackPlacements: [
                                    "top-end",
                                    "bottom-start"
                                ]
                            }
                        }
                    ]
                });
            };
            const hide = () => {
                tip.style.opacity = "0";
                tip.style.visibility = "hidden";
                if (popper) {
                    popper.destroy();
                    popper = null;
                }
            };
            wrap.addEventListener("mouseenter", show);
            wrap.addEventListener("mouseleave", hide);
            // учтём очистку при разрушении виджета
            this._yearTipCleanups.push(() => {
                wrap.removeEventListener("mouseenter", show);
                wrap.removeEventListener("mouseleave", hide);
                if (popper) {
                    popper.destroy();
                    popper = null;
                }
            });
        });
    }
    // ───────────────────────────── destroy ─────────────────────────────
    /**
     * Очистка ресурсов/подписок.
     */
    _onDestroy() {
        window.removeEventListener("keydown", this._onKeyCtrlArrows);
        window.removeEventListener("popstate", this._onPopState);
        window.removeEventListener("resize", this._onResize);
        if (this._onDocDownFilter) {
            document.removeEventListener("mousedown", this._onDocDownFilter);
            this._onDocDownFilter = undefined;
        }
        this._abortController.abort();
        // Popper cleanups
        for (const fn of this._yearTipCleanups)
            fn();
        this._yearTipCleanups = [];
        if (this._playerAbortController)
            this._playerAbortController.abort();
    }
}
FileManagerWidget._alias = "FileManager";

export { FileManagerWidget };
